import React, { useState } from 'react';
import { Plus, Eye, Edit2, Trash2 } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatDate, formatCurrency, generateId } from '../../utils/helpers';

const emptyForm = { firstName:'', lastName:'', dob:'', gender:'male', phone:'', email:'', address:'', qualification:'', designation:'Teacher', department:'', joinDate: new Date().toISOString().split('T')[0], salary:'', status:'active', isClassTeacher:false, classTeacherOf:'' };

function StaffList() {
  const { staff, setStaff, classes } = useApp();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);

  const openAdd = () => { setForm({...emptyForm, employeeId:`EMP${String(staff.length+1).padStart(3,'0')}`}); setModal('add'); };
  const openEdit = (s) => { setForm(s); setSelected(s); setModal('edit'); };
  const openView = (s) => { setSelected(s); setModal('view'); };
  const remove = (id) => { if(window.confirm('Delete this staff?')) setStaff(prev=>prev.filter(s=>s.id!==id)); };

  const save = () => {
    if (modal==='add') setStaff(prev=>[...prev,{...form,id:generateId('STF'),subjectIds:[],classIds:[]}]);
    else setStaff(prev=>prev.map(s=>s.id===selected.id?form:s));
    setModal(null);
  };

  const getClass = (id) => { const c = classes.find(c=>c.id===id); return c?`${c.name}-${c.section}`:'-'; };

  const columns = [
    { label: 'Staff', key: 'name', render: s => (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold flex-shrink-0">
          {s.firstName?.[0]}{s.lastName?.[0]}
        </div>
        <div><p className="font-medium">{s.firstName} {s.lastName}</p><p className="">{s.employeeId}</p></div>
      </div>
    )},
    { label: 'Designation', accessor: 'designation', sortable: true },
    { label: 'Department', accessor: 'department', sortable: true },
    { label: 'Phone', accessor: 'phone' },
    { label: 'Class Teacher', key: 'ct', render: s => s.isClassTeacher ? <span className="text-green-600 font-medium">{getClass(s.classTeacherOf)}</span> : <span className="text-gray-400">No</span> },
    { label: 'Status', key: 'status', render: s => <Badge value={s.status} /> },
  ];

  const formFields = (
    <div className="grid grid-cols-2 gap-4">
      {[['First Name','firstName','text'],['Last Name','lastName','text'],['DOB','dob','date'],['Phone','phone','tel'],['Email','email','email'],['Qualification','qualification','text'],['Department','department','text'],['Salary','salary','number'],['Join Date','joinDate','date'],['Address','address','text']].map(([label,key,type]) => (
        <div key={key} className={key==='address'?'col-span-2':''}>
          <label className="block text-sm font-medium mb-1">{label}</label>
          <input type={type} value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}
            className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        </div>
      ))}
      <div>
        <label className="block text-sm font-medium mb-1">Gender</label>
        <select value={form.gender||'male'} onChange={e=>setForm(f=>({...f,gender:e.target.value}))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          {['male','female','other'].map(o=><option key={o}>{o}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Designation</label>
        <select value={form.designation||''} onChange={e=>setForm(f=>({...f,designation:e.target.value}))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          {['Principal','Vice Principal','Senior Teacher','Teacher','Librarian','Driver','Support Staff'].map(o=><option key={o}>{o}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Status</label>
        <select value={form.status||'active'} onChange={e=>setForm(f=>({...f,status:e.target.value}))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
          {['active','inactive'].map(o=><option key={o}>{o}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input type="checkbox" id="ct" checked={!!form.isClassTeacher} onChange={e=>setForm(f=>({...f,isClassTeacher:e.target.checked}))} className="w-4 h-4 accent-indigo-600" />
        <label htmlFor="ct" className="text-sm">Is Class Teacher</label>
      </div>
      {form.isClassTeacher && (
        <div>
          <label className="block text-sm font-medium mb-1">Class Teacher Of</label>
          <select value={form.classTeacherOf||''} onChange={e=>setForm(f=>({...f,classTeacherOf:e.target.value}))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            <option value="">Select Class</option>
            {classes.map(c=><option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
          </select>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Staff</h1><p className="text-gray-500 text-sm mt-0.5">{staff.length} staff members</p></div>
        <button onClick={openAdd} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1 rounded-lg text-sm font-medium transition-colors">
          <Plus size={16} /> Add Staff
        </button>
      </div>
      <DataTable columns={columns} data={staff}
        actions={row=><>
          <button onClick={()=>openView(row)} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600"><Eye size={15}/></button>
          <button onClick={()=>openEdit(row)} className="p-1.5 hover:bg-indigo-50 rounded-lg text-indigo-600"><Edit2 size={15}/></button>
          <button onClick={()=>remove(row.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"><Trash2 size={15}/></button>
        </>}
      />
      <Modal isOpen={modal==='add'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='add'?'Register Staff':'Edit Staff'} size="xl">
        {formFields}
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={()=>setModal(null)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} className="px-4 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
        </div>
      </Modal>
      <Modal isOpen={modal==='view'} onClose={()=>setModal(null)} title="Staff Profile" size="lg">
        {selected && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-2xl font-bold">
                {selected.firstName?.[0]}{selected.lastName?.[0]}
              </div>
              <div>
                <h3 className="text-xl font-bold">{selected.firstName} {selected.lastName}</h3>
                <p className="text-gray-500">{selected.designation} • {selected.department}</p>
                <Badge value={selected.status} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-lg p-4">
              {[['Employee ID',selected.employeeId],['Phone',selected.phone],['Email',selected.email],['DOB',formatDate(selected.dob)],['Qualification',selected.qualification],['Join Date',formatDate(selected.joinDate)],['Salary',formatCurrency(selected.salary)],['Address',selected.address]].map(([k,v])=>(
                <div key={k}><p className="font-medium">{k}</p><p className="text-sm font-medium">{v||'-'}</p></div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
export default StaffList;
